import React from 'react'

import cn from 'classnames'

import { MarginRuler, StickyRulerWrapper } from '#components/MarginRuler'
import { useDebouncedElementSize } from '#hooks'

import styles from './PrintPreviewWrapper.module.scss'

// page editor is 8.5" inches
const PAGE_WIDTH_PX = 8.5 * 96
// we put 50px padding on either side for avatars on left & balance on right
const AFFORDANCE_PADDING_PX = 50
// this is the what the print preview wrapper needs to wrap. It's scaled by the
// zoom level.
const UNSCALED_EDITOR_WIDTH =
  AFFORDANCE_PADDING_PX + PAGE_WIDTH_PX + AFFORDANCE_PADDING_PX

export const PrintPreviewWrapper = ({
  zoomLevel,
  children,
  isStructural,
  hasLockedPages,
  showMarginRuler,
}: {
  zoomLevel: number
  children: React.ReactNode
  isStructural: boolean
  hasLockedPages: boolean
  showMarginRuler?: boolean
}) => {
  const { ref: boundingRef, width: boundingWidth } = useDebouncedElementSize()
  // Because we're scaling the editor within the scroller, mantine's hook
  // doesn't calculate its width properly, but we can use getBoundingClientRec
  // instead
  const editorWidth = UNSCALED_EDITOR_WIDTH * zoomLevel
  // const freeHorizontalSpace = scrollerWidth - editorWidth
  // const paddingLeft = freeHorizontalSpace > 0 ? freeHorizontalSpace / 2 : 0
  const scrollNeeded = editorWidth > boundingWidth
  // css is weirdly different for zoom shrinking vs magnification,
  // so we scale the containers differently
  const shrunk = zoomLevel < 1

  return (
    <div
      ref={boundingRef}
      style={{
        '--unscaled-editor-width': `${UNSCALED_EDITOR_WIDTH}px`,
      }}
      className={cn(styles.printPreviewWrapper, {
        [styles.structuralPagination]: isStructural,
        [styles.inlinePagination]: !isStructural,
        [styles.hasLockedPages]: hasLockedPages,
      })}
    >
      <div
        className={cn(styles.scriptScrollWrapper, {
          [styles.noScroll]: !scrollNeeded,
          [styles.scroll]: scrollNeeded,
          [styles.shrunk]: shrunk,
        })}
        data-editor-scroller
      >
        {showMarginRuler && (
          <StickyRulerWrapper
            zoomLevel={zoomLevel}
            positioning={scrollNeeded ? 'left' : 'center'}
            leftAffordance={AFFORDANCE_PADDING_PX}
          >
            <MarginRuler />
          </StickyRulerWrapper>
        )}
        <div className={cn(styles.scriptCenteringBox)}>
          <div className={styles.scaledScriptWrapper}>{children}</div>
        </div>
      </div>
    </div>
  )
}
